import {weakMemo, cacheMemo} from '@packages/utilities/memo';
import {type Pair, pair, getFirst, getSecond} from '@packages/utilities/Pair';
import {
  type LazyList,
  toArray,
  take,
  singleton,
  empty,
  isNotEmpty,
  map,
  flatMap,
  filter,
  entries,
  lookup,
  concat,
} from '@packages/utilities/lazy-list';
import type {IconComponent} from '@packages/systems/spring';
import {
  type Type,
  Types,
  checkType,
  inferType,
  substituteType,
  type CheckerState,
  type CheckerEnvironment,
  setEnvironment,
  getSubstitution,
  type ExpressionPath,
  VariableMap,
  Variable,
  Plugins,
  Expressions,
  getSystem,
  EnvTypes,
  subsumes,
  instantiateGenerics,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {
  type Expression,
  ESelect,
  ECall,
  EVariable,
  EHole,
} from '@packages/systems/wfdl-ast';
import {
  type Identifier,
  type PluginName,
  qualify,
  getPluginName,
  getName,
  QualifiedMap,
} from '@packages/systems/wfdl-Qualified';
import {emptyArray} from '@packages/utilities/fp/utils';
import {getCurrentOrDefaultExpression} from '@packages/systems/core/component-conversions';
import {SerializedType, serializeType, unserializeType} from './serializedtype';

type ImplicitLazyItem = {
  finalExpr: Expression;
  state: CheckerState;
  argTy: Type;
};
type LazyListImplicits = LazyList<ImplicitLazyItem>;

type LocalLazyItem = Pair<Identifier, Binding>;
type PluginsLazyItem = Pair<PluginName, LazyList<LocalLazyItem>>;
export type AvailableBindings = {
  local: LazyList<LocalLazyItem>;
  plugins: LazyList<PluginsLazyItem>;
};

export type Binding = {
  expr: Expression | null;
  implicits: Array<Expression>;
  children: Map<string, Binding>;
  exprForLabelAndIcon?: Expression;
  icon?: IconComponent;
  expandOnInitialOpen?: boolean;
};

type LocalValues = Record<Identifier, EnvTypes.NormalizedEnvType>;
type PluginValues = QualifiedMap<Identifier, EnvTypes.NormalizedEnvType>;

type ImplicitValue = 'prefilledArgs' | 'holeArg' | 'no';

const CALL_ARG_PATH: ExpressionPath = [{in: 'Call', at: 'arg'}];
const emptyMap = new Map();

export const emptyBinding: Binding = {
  expr: null,
  implicits: emptyArray,
  children: emptyMap,
};

export const emptyAvailableBindings: AvailableBindings = {
  local: empty(),
  plugins: empty(),
};

const notEmptyBinding = (entry: Pair<Identifier, Binding>): boolean =>
  getSecond(entry) !== emptyBinding;

const hasBindings = (entry: PluginsLazyItem): boolean =>
  isNotEmpty(getSecond(entry));

export const hasAvailableBindings = (available: AvailableBindings): boolean =>
  isNotEmpty(available.local) || isNotEmpty(available.plugins);

export const getBinding = (
  available: AvailableBindings,
  variable: Variable.VariableType
): undefined | Binding => {
  return Variable.match({
    unqualified: (id) => lookup(available.local, id),
    qualified: (qname) => {
      const pluginBindings = lookup(available.plugins, getPluginName(qname));
      if (!pluginBindings) return undefined;
      return lookup(pluginBindings, getName(qname));
    },
  })(variable);
};

const isMatch = weakMemo((state: CheckerState) =>
  cache100((ty: SerializedType) =>
    cache100((refTy: SerializedType) => {
      const errors = getSecond(
        subsumes(unserializeType(refTy), unserializeType(ty))(state)
      );
      return errors.length === 0;
    })
  )
);

/**
 * This function answers the question: Given an Expression and a reference to a
 * variable, can that variable, or anything from within it, be bound in place of
 * this Expression.
 */
const findBindings = (
  /**
   * Type of the ContextualExpression against which we want to find potential bindings from our state
   */
  ty: Type,
  /**
   * The Type for the value we are checking if it can be bound
   */
  refType: Type,
  /**
   * The Expression binding (Select, Variable, Call) for the value we are checking if it can be bound
   */
  refExpression: Expression,
  /**
   * The CheckerState which holds all the relevant information about potential
   * unknowns and potential errors in the TypeChecker. This is used to determine
   * if a Variable of some certain Type can replace the `expression`
   */
  state: CheckerState,
  /**
   * Implicits that can bind to the expression in question
   */
  implicits: LazyListImplicits,
  /**
   * We need to track if we are binding on top of a prebindable function,
   * since for now when prebindable is not officially supported we only
   * want one layer of binding.
   */
  ignoreFunctionBindings: boolean = false
): Binding => {
  // Skip prebinding functions to prebindable functions.
  // For now we only want to bind "one layer" of functions.
  if (ignoreFunctionBindings && Types.getFunctionArgAndRetTypes(refType)) {
    return emptyBinding;
  }

  // We don’t allow binding to values of Unknown type.
  // Values of Unknown types would be bindable everywhere since these values
  // type-check in every position and would thus create an overwhelming
  // amount of binding targets.
  if (Types.isUnknownType(refType)) {
    return emptyBinding;
  }

  // Values that have a Type level system flag hiddenProperty should not be
  // shown as binding options
  const sys = getSystem(refType);
  if (sys && sys.hiddenProperty) {
    return emptyBinding;
  }

  const isCurrentTypeRTC = Types.isRichTextChildrenType(ty);
  const isRefTypeRTC = Types.isRichTextChildrenType(refType);

  // We don't allow binding to SlotContent for now. We're hiding this property until after
  // Slots GA and we figure out how to handle it.
  // https://webflow.slack.com/archives/C05HB33TFHP/p1700158635683499
  const isRefTypeSlotContent = Types.isSlotContentType(refType);
  if (isRefTypeSlotContent) {
    // Exception: Allow bindings to SlotContent when selecting condition fields
    const isTargetOperand = Types.isConditionsOperandType(ty);
    const isTargetSlotContent = Types.isSlotContentType(ty);
    if (!isTargetOperand && !isTargetSlotContent) {
      return emptyBinding;
    }
  }

  // We only allow binding Alt Text reference types to Alt Text
  const isCurrentTypeAltText = Types.isAltTextType(ty);
  const isRefTypeAltText = Types.isAltTextType(refType);
  if (!isCurrentTypeAltText && isRefTypeAltText) {
    return emptyBinding;
  }

  if (isCurrentTypeRTC && isRefTypeRTC) {
    // This is a special case where we know that we dont need to look at records or functions,
    // so we can return early
    return {
      expr: refExpression,
      implicits: emptyArray,
      children: new Map(),
    };
  }

  const exprResult =
    // For non-RTC we rely on type checking
    !isCurrentTypeRTC &&
    !isRefTypeRTC &&
    isMatch(state)(serializeType(ty))(serializeType(refType))
      ? refExpression
      : null;

  // Find potential binding targets from within the fields of the provided
  // variable
  const recordFields = Types.getRecordFields(refType);
  const childrenResult: Map<string, Binding> = recordFields
    ? Object.keys(recordFields).reduce<Map<string, Binding>>((acc, curr) => {
        const fieldType = recordFields[curr];
        if (!fieldType) return acc;
        const matchesForField = findBindings(
          ty,
          fieldType,
          ESelect({
            from: refExpression,
            prop: curr,
          }),
          state,
          implicits,
          ignoreFunctionBindings
        );
        if (matchesForField === emptyBinding) return acc;
        if (acc === emptyMap) acc = new Map();

        // Special case handling for mocking a nested record UI if the system.group is set
        const fieldSys = getSystem(fieldType);
        if (fieldSys && fieldSys.group) {
          const {recordKey} = fieldSys.group;

          // when group is set we want to use a real Binding instead
          // of a virtual record to group the subfields if possible and
          // group.recordKey should match the intended field that those
          // subfields are grouped under
          let existingGroup: Binding | undefined = acc.get(recordKey);

          if (!existingGroup) {
            // if group is set but no Binding corresponds to group.recordKey,
            // we try to construct a virtual record based on the Type
            // matching group.recordKey among recordFields
            existingGroup = {
              expr: null,
              implicits: emptyArray,
              children: emptyMap,
            };

            const existingRecordField = recordFields[recordKey];
            if (existingRecordField) {
              // this allows our virtual record to render an icon and label
              // based on the type of the field corresponding to
              // group.recordKey
              existingGroup.exprForLabelAndIcon = ESelect({
                from: refExpression,
                prop: recordKey,
              });
            }
          }

          const existingChildren = existingGroup?.children;
          const existingExpr = existingGroup?.expr;
          const existingImplicits = existingGroup?.implicits;
          const existingExprForLabelAndIcon =
            existingGroup?.exprForLabelAndIcon;
          let newChildren;
          // If there exists a group under this name we want to append this entry under it.
          // We check that existingChildren is not the same as emptyMap. We use emptyMap
          // as a constant for empty Map objects, but it should never be mutated. So here
          // we make sure that it is not the original emptyMap before adding more elements
          // into the Map.
          if (existingChildren && existingChildren !== emptyMap) {
            existingChildren.set(curr, matchesForField);
            newChildren = existingChildren;
          } else {
            newChildren = new Map([[curr, matchesForField]]);
          }

          const updatedGroup: Binding = {
            implicits: existingImplicits || emptyArray,
            expr: existingExpr || null,
            children: newChildren,
          };

          if (existingExprForLabelAndIcon) {
            updatedGroup.exprForLabelAndIcon = existingExprForLabelAndIcon;
          }

          acc.set(recordKey, updatedGroup);
          return acc;
        }

        const existingGroup = acc.get(curr);
        if (existingGroup) {
          // If there is already a group under this key, it means some
          // other field has group.recordKey matching curr and has created
          // a group. We want to use actual binding match with the existing
          // group's children included.
          let existingChildren = existingGroup.children;
          const matchChildren = matchesForField.children;

          if (existingChildren === emptyMap) {
            // make sure we don't write into emptyMap
            existingChildren = new Map();
          }

          for (const [key, value] of matchChildren) {
            existingChildren.set(key, value);
          }

          const updatedGroup: Binding = {
            ...matchesForField,
            children: existingChildren,
          };
          acc.set(curr, updatedGroup);
          return acc;
        }

        acc.set(curr, matchesForField);
        return acc;
      }, emptyMap)
    : emptyMap;

  // Return the resulting bindings if we have a binding available
  // to a function, so we don't return fn and fn() as a result
  if (exprResult) {
    return {
      implicits: emptyArray,
      expr: exprResult,
      children: childrenResult,
    };
  }

  // Try with all implicits
  const implicitResults = flatMap<ImplicitLazyItem, Expression>(
    implicits,
    (implicit) => {
      const matchToArg = findBindings(
        implicit.argTy,
        refType,
        refExpression,
        implicit.state,
        empty(),
        true
      );
      if (matchToArg.expr === null) return empty();
      return singleton(
        Expressions.setIn(implicit.finalExpr, CALL_ARG_PATH, matchToArg.expr)
      );
    }
  );

  // Find potential binding targets if the variable is a Function by prepending
  // it with a Call.
  const [newState, body] = instantiateGenerics(refType)(state);
  const argRet = Types.getFunctionArgAndRetTypes(body);
  if (argRet) {
    return findBindings(
      ty,
      argRet.ret,
      ECall({
        fun: refExpression,
        arg: EHole(),
      }),
      newState,
      implicits,
      ignoreFunctionBindings
    );
  }

  if (isNotEmpty(implicitResults) || childrenResult.size > 0) {
    return {
      // We currently only use 1 implicit
      implicits: toArray(take(1, implicitResults)),
      expr: null,
      children: childrenResult,
    };
  }
  return emptyBinding;
};

/**
 * If we need to re-infer the whole environment with a Hole at path we call this
 * function. Provides us with a "fresh" ContextualExpression and CheckerState.
 *
 * For example if we have:
 *
 * `EList([ENumber[5)]])` with a Type of `TList(TTypeReference('a'))`
 *
 * We need to  replace the Expression with a Hole to get all true "lists of
 * anything" instead of relying on our previous inference which lead to "lists
 * of numbers"
 */
export const replaceExprWithHoleAndReInfer = (
  expr: ContextualExpression,
  path: ExpressionPath,
  state: CheckerState
): [CheckerState, ContextualExpression] => {
  // set a Hole to the spot we are looking at
  const exprWithHoles = Expressions.setIn(stripAll(expr), path, EHole());
  // inferType with the exprWithHoles to get a state with substitutions
  return inferType(exprWithHoles)(state);
};

const isVisibleValue = (
  envType: EnvTypes.NormalizedEnvType,
  pluginName: PluginName | null
) =>
  envType.exported === 'visible' ||
  (pluginName === '__SitePlugin' && envType.exported !== 'hidden');

const isImplicitValue = (envType: EnvTypes.NormalizedEnvType) =>
  envType.implicit === 'prefilledArgs' || envType.implicit === 'holeArg';

const mapImplicitValues = (
  implicitValues: LazyList<Pair<string, EnvTypes.NormalizedEnvType>>,
  pluginName: PluginName | null
) => {
  return map(implicitValues, (entry) => {
    const valName = getFirst(entry);
    const type = getSecond(entry);
    let source;
    if (pluginName !== null) {
      source = Variable.qualified(qualify(pluginName, valName));
    } else {
      source = Variable.unqualified(valName);
    }
    return {
      source: EVariable(source),
      type: type.dataType,
      implicitValue: type.implicit,
    };
  });
};

const getAllLocalImplicitValues = weakMemo(
  (localValues: LocalValues): LazyList<ViableImplicit> => {
    const implicitValues = filter(entries(localValues), (entry) => {
      const type = getSecond(entry);
      return isImplicitValue(type) && isVisibleValue(type, null);
    });
    return mapImplicitValues(implicitValues, null);
  }
);

const getAllImplicitValues = weakMemo(
  (pluginsValues: PluginValues): LazyList<ViableImplicit> =>
    flatMap(entries(pluginsValues.byPlugin()), (pluginEntry) => {
      const pluginName = getFirst(pluginEntry);
      const pluginValues = getSecond(pluginEntry);
      const implicitValues = filter(entries(pluginValues), (entry) => {
        const type = getSecond(entry);
        return isImplicitValue(type) && isVisibleValue(type, pluginName);
      });
      return mapImplicitValues(implicitValues, pluginName);
    })
);

const collectViableImplicitFactory =
  (plugins: Plugins.PluginsType, state: CheckerState, ty: Type) =>
  ({source, type, implicitValue}: ViableImplicit): LazyListImplicits => {
    const match = findBindings(
      ty,
      substituteType(getSubstitution(state))(type),
      source,
      state,
      empty()
    );

    const matchExpr = match.expr;
    if (matchExpr === null) return empty();
    const call = Expressions.deconstructCall(matchExpr);
    const callArgs = call.args;
    // We assume that prebindable env values only provide 1 match,
    // and that the match Expression is a function call
    if (callArgs.length < 1) return empty();

    // Fill argument default values in place of holes in order to make sure the
    // implicitly bound function is "ready to use" after being bound
    const {args} = Types.deconstructFunctionType(type);

    // We'll just use the existing holes for arguments that explicit request it
    const finalCallArgs =
      implicitValue === 'holeArg'
        ? callArgs
        : callArgs.map((arg, idx) => {
            if (idx >= callArgs.length - 1) return arg;
            const curr = args[idx];
            if (!curr) return arg;
            return getCurrentOrDefaultExpression(plugins)(curr);
          });
    const finalExpr = Expressions._call(call.fun, finalCallArgs);

    const [checkTypeState, nextContextualExpr] = checkType(
      finalExpr,
      ty
    )(state);

    const argOnPrebindable = ContextualExpressions.getIn(
      nextContextualExpr,
      CALL_ARG_PATH
    );

    if (!argOnPrebindable) {
      // we should always have an argument to prebind the implicit funtion to
      return empty();
    }

    const argTy = getAnn(argOnPrebindable).type;
    if (!argTy) return empty();

    return singleton({
      finalExpr,
      state: checkTypeState,
      argTy,
    });
  };

type ViableImplicit = {
  source: Expression;
  type: Type;
  implicitValue: ImplicitValue;
};
type CollectFn = ({
  source,
  type,
  implicitValue,
}: ViableImplicit) => LazyListImplicits;

const collectPluginImplicits = weakMemo((pluginsValues: PluginValues) =>
  weakMemo((fn: CollectFn) => flatMap(getAllImplicitValues(pluginsValues), fn))
);

const collectLocalImplicits = weakMemo((localValues: LocalValues) =>
  weakMemo((fn: CollectFn) =>
    flatMap(getAllLocalImplicitValues(localValues), fn)
  )
);

const collectLocalResult =
  (state: CheckerState, implicits: LazyListImplicits, ty: Type) =>
  (
    entry: Pair<Identifier, EnvTypes.NormalizedEnvType>
  ): Pair<string, Binding> => {
    const varName = getFirst(entry);
    const type = getSecond(entry);
    // Skip implicit values, we have gathered them to an array already
    if (isImplicitValue(type)) {
      return pair(varName, emptyBinding);
    }
    return pair(
      varName,
      findBindings(
        ty,
        substituteType(getSubstitution(state))(type.dataType),
        EVariable(Variable.unqualified(varName)),
        state,
        implicits
      )
    );
  };

const collectResults = (
  values: LocalValues,
  fn: (arg1: Pair<Identifier, EnvTypes.NormalizedEnvType>) => LocalLazyItem
): LazyList<LocalLazyItem> => {
  const res = map(entries(values), fn);
  return filter(res, notEmptyBinding);
};

const collectPluginValue =
  (state: CheckerState, implicits: LazyListImplicits, ty: Type) =>
  (pluginEntry: Pair<PluginName, LocalValues>) =>
  (
    entry: Pair<Identifier, EnvTypes.NormalizedEnvType>
  ): Pair<string, Binding> => {
    const pluginName = getFirst(pluginEntry);
    const valName = getFirst(entry);
    const type = getSecond(entry);
    if (!isVisibleValue(type, pluginName)) return pair(valName, emptyBinding);
    // Skip implicit values, we have gathered them to an array already
    if (isImplicitValue(type)) {
      return pair(valName, emptyBinding);
    }

    return pair(
      valName,
      findBindings(
        ty,
        substituteType(getSubstitution(state))(type.dataType),
        EVariable(Variable.qualified(qualify(pluginName, valName))),
        state,
        implicits
      )
    );
  };

const collectPluginResult =
  (state: CheckerState, implicits: LazyListImplicits, ty: Type) =>
  (pluginEntry: Pair<PluginName, LocalValues>): PluginsLazyItem => {
    const pluginName = getFirst(pluginEntry);
    const pluginValues = getSecond(pluginEntry);
    const res = collectResults(
      pluginValues,
      collectPluginValue(state, implicits, ty)(pluginEntry)
    );

    return pair(pluginName, res);
  };

const collectPluginResults = (
  pluginsValues: PluginValues,
  fn: (arg1: Pair<PluginName, LocalValues>) => PluginsLazyItem
): LazyList<PluginsLazyItem> => {
  const pluginResults = map(entries(pluginsValues.byPlugin()), fn);
  return filter(pluginResults, hasBindings);
};

const setContext = weakMemo((context: CheckerEnvironment['context']) =>
  weakMemo((state: CheckerState) =>
    setEnvironment({context, expressionPath: emptyArray})(state)
  )
);

const cache100 = cacheMemo(100);

export const findAvailableBindingsMemo = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((currState: CheckerState) =>
      weakMemo((context: CheckerEnvironment['context']) =>
        cache100((wantedType: SerializedType): AvailableBindings => {
          // Make sure we use the values from the environment of `expression` we are
          // focusing on and on the top-level expression
          const state = setContext(context)(currState);
          const values = context.values;
          const localValues = VariableMap.getLocal(values);
          const pluginsValues =
            VariableMap.getPlugins<EnvTypes.NormalizedEnvType>(values);

          const ty = unserializeType(wantedType);

          const collectViableImplicit = collectViableImplicitFactory(
            plugins,
            state,
            ty
          );

          const pluginImplicits = collectPluginImplicits(pluginsValues)(
            collectViableImplicit
          );
          const localImplicits = collectLocalImplicits(localValues)(
            collectViableImplicit
          );
          const allImplicits = concat(pluginImplicits, localImplicits);

          const localResults = collectResults(
            localValues,
            collectLocalResult(state, allImplicits, ty)
          );

          const pluginResults = collectPluginResults(
            pluginsValues,
            collectPluginResult(state, allImplicits, ty)
          );

          return {
            local: localResults,
            plugins: pluginResults,
          };
        })
      )
    )
);

/**
 * This function answers the question: Given a typed hole (of type `dataType`)
 * Expression, what options are there on the environment for filling this hole?
 */
export const findAvailableBindings = (
  /**
   * The ContextualExpression against which we want to find potential bindings from our state
   */
  expression: ContextualExpression,
  /**
   * The CheckerState which holds all the relevant information about potential
   * unknowns and potential errors in the TypeChecker. This is used to determine
   * if a Variable of some certain Type can replace the `expression`
   */
  currState: CheckerState,
  /**
   * Current plugins in scope
   */
  // ignoreFunctionBindings: boolean = false
  plugins: Plugins.PluginsType
): AvailableBindings => {
  const ty = getAnn(expression).type;

  if (!ty) throw new Error('No type provided on Annotation');

  return findAvailableBindingsMemo(plugins)(currState)(
    getAnn(expression).env.context
  )(serializeType(ty));
};
